import React, { useEffect, useState } from 'react';
import {
  Box, Heading, Button, HStack, Image, useToast, IconButton, Switch, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, FormControl, FormLabel, Input, Textarea, Select, Spinner, Flex, Text, SimpleGrid
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { supabase } from '../../services/supabaseClient';
import AdminLayout from './AdminLayout';

const initialForm = {
  nome: '',
  descricao: '',
  valor: '',
  imagem_url: '',
  categoria: '',
  ativo: true,
};

const ProductsAdminPage = () => {
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [novaCategoria, setNovaCategoria] = useState("");
  const [adicionandoCategoria, setAdicionandoCategoria] = useState(false);
  const [mostrarCampoNovaCategoria, setMostrarCampoNovaCategoria] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  // Buscar produtos
  useEffect(() => {
    fetchProdutos();
    fetchCategorias();
  }, []);

  async function fetchProdutos() {
    setLoading(true);
    const { data, error } = await supabase.from('produtos').select('*').order('criado_em', { ascending: false });
    if (!error) setProdutos(data);
    setLoading(false);
  }

  async function fetchCategorias() {
    const { data } = await supabase.from('categorias_produtos').select('*').order('nome');
    setCategorias(data || []);
  }

  async function handleAddCategoria() {
    if (!novaCategoria.trim()) return;
    setAdicionandoCategoria(true);
    try {
      const { data, error } = await supabase.from('categorias_produtos').insert([{ nome: novaCategoria.trim() }]).select();
      if (!error && data && data.length > 0) {
        setCategorias(prev => [...prev, data[0]]);
        setForm(f => ({ ...f, categoria: data[0].nome }));
        setNovaCategoria("");
        setMostrarCampoNovaCategoria(false);
        toast({ title: "Categoria adicionada!", status: "success", duration: 2000 });
      } else {
        toast({ title: "Erro ao adicionar categoria", description: error?.message, status: "error" });
      }
    } catch (error) {
      toast({ title: "Erro ao adicionar categoria", description: error.message, status: "error" });
    } finally {
      setAdicionandoCategoria(false);
    }
  }

  function openModal(produto = null) {
    if (produto) {
      setForm(produto);
      setEditingId(produto.id);
    } else {
      setForm(initialForm);
      setEditingId(null);
    }
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setForm(initialForm);
    setEditingId(null);
  }

  async function handleSave() {
    if (!form.nome || !form.valor || !form.categoria) {
      toast({ title: 'Preencha todos os campos obrigatórios', status: 'warning' });
      return;
    }
    setSaving(true);
    try {
      let result;
      if (editingId) {
        result = await supabase.from('produtos').update(form).eq('id', editingId);
      } else {
        result = await supabase.from('produtos').insert([form]);
      }
      if (!result.error) {
        toast({ title: 'Produto salvo com sucesso', status: 'success' });
        fetchProdutos();
        closeModal();
      } else {
        toast({ title: 'Erro ao salvar produto', description: result.error.message, status: 'error' });
      }
    } catch (error) {
      toast({ title: 'Erro ao salvar produto', description: error.message, status: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Tem certeza que deseja excluir este produto?')) return;
    const { error } = await supabase.from('produtos').delete().eq('id', id);
    if (!error) {
      toast({ title: 'Produto excluído', status: 'success' });
      fetchProdutos();
    }
  }

  async function handleImageUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const ext = file.name.split('.').pop();
    const fileName = `${form.nome.replace(/\s+/g, '_').toLowerCase()}_${Date.now()}.${ext}`;
    const { data: uploadData, error: uploadError } = await supabase.storage.from('produtos').upload(fileName, file, { upsert: true });
    if (uploadError) {
      toast({ title: 'Erro ao enviar imagem', description: uploadError.message, status: 'error' });
      setUploading(false);
      return;
    }
    const { data: publicUrlData } = supabase.storage.from('produtos').getPublicUrl(fileName);
    setForm({ ...form, imagem_url: publicUrlData.publicUrl });
    setUploading(false);
  }

  return (
    <AdminLayout>
      <Box w="100%" maxW="900px" mx="auto" px={{ base: 2, md: 8 }} py={8}>

      <Flex justify="space-between" align="center" mb={6} direction={{ base: 'column', sm: 'row' }} gap={4}>
        <Heading fontSize={{ base: '2xl', md: '3xl' }}>Produtos</Heading>
        <Button leftIcon={<AddIcon />} colorScheme="yellow" onClick={() => openModal()}>Novo Produto</Button>
      </Flex>

      {loading ? <Spinner size="lg" mt={10} /> : (
        <Box overflowX="auto">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {produtos.map((p) => (
              <Box 
                key={p.id} 
                bg="gray.800" 
                borderRadius="xl" 
                p={5} 
                boxShadow="md" 
                display="flex" 
                flexDir="column" 
                gap={2}
              >
                <Flex justify="space-between" align="center">
                  <Text fontWeight="bold" fontSize="xl" color="yellow.300">{p.nome}</Text>
                  <Flex gap={2}>
                    <IconButton aria-label="Editar" icon={<EditIcon />} size="sm" colorScheme="yellow" variant="ghost" onClick={() => openModal(p)} />
                    <IconButton aria-label="Excluir" icon={<DeleteIcon />} size="sm" colorScheme="red" variant="ghost" onClick={() => handleDelete(p.id)} />
                  </Flex>
                </Flex>
                
                <Flex align="center" mt={2}>
                  {p.imagem_url ? (
                    <Image src={p.imagem_url} boxSize="80px" objectFit="cover" borderRadius="md" mr={4} />
                  ) : (
                    <Box w="80px" h="80px" bg="gray.700" borderRadius="md" mr={4} />
                  )}
                  <Box>
                    <Text color="whiteAlpha.800">Categoria: <b>{p.categoria || '-'}</b></Text>
                    <Text color="yellow.300" fontWeight="bold" fontSize="lg" mt={1}>R$ {Number(p.valor).toFixed(2)}</Text>
                    <Flex align="center" mt={1}>
                      <Text color="whiteAlpha.800" mr={2}>Status:</Text>
                      <Switch isChecked={p.ativo} isReadOnly colorScheme="yellow" size="md" />
                    </Flex>
                  </Box>
                </Flex>
                
                {p.descricao && (
                  <Text color="whiteAlpha.700" fontSize="sm" mt={1}>{p.descricao}</Text>
                )}
              </Box>
            ))}
          </SimpleGrid>
          {produtos.length === 0 && (
            <Text color="whiteAlpha.700" textAlign="center" mt={8}>Nenhum produto cadastrado ainda.</Text>
          )}
        </Box>
      )}

      {/* Modal de cadastro/edição */}
      <Modal isOpen={modalOpen} onClose={closeModal} size={{ base: "md", md: "lg" }} isCentered>
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(5px)" />
        <ModalContent 
          bg="gray.800" 
          color="white" 
          borderRadius="xl" 
          boxShadow="xl"
          mx={{ base: 3, md: 0 }}
          my={{ base: 3, md: 0 }}
          maxH={{ base: "calc(100vh - 80px)", md: "auto" }}
          overflowY="auto"
        >
          <ModalHeader borderBottomWidth="1px" borderColor="gray.700" py={4} px={6}>
            <Flex align="center">
              {editingId ? (
                <EditIcon color="yellow.300" mr={3} />
              ) : (
                <AddIcon color="yellow.300" mr={3} />
              )}
              <Text fontSize="xl" fontWeight="bold" color="white">
                {editingId ? 'Editar Produto' : 'Novo Produto'}
              </Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={6} px={{ base: 4, md: 6 }}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
              <FormControl isRequired>
                <FormLabel fontWeight="medium" color="whiteAlpha.900">Nome do Produto</FormLabel>
                <Input 
                  value={form.nome} 
                  onChange={e => setForm({ ...form, nome: e.target.value })} 
                  bg="gray.700" 
                  color="white" 
                  borderColor="gray.600"
                  _hover={{ borderColor: "yellow.300" }}
                  _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                  placeholder="Ex: Pomada Modeladora"
                />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel fontWeight="medium" color="whiteAlpha.900">Valor (R$)</FormLabel>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.valor}
                  onChange={e => setForm({ ...form, valor: e.target.value })}
                  bg="gray.700" 
                  color="white" 
                  borderColor="gray.600"
                  _hover={{ borderColor: "yellow.300" }}
                  _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                  placeholder="0,00"
                />
                {form.valor && (
                  <Text fontSize="sm" color="yellow.300" mt={1}>
                    {Number(form.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </Text>
                )}
              </FormControl>
            </SimpleGrid>
            
            <FormControl mb={5} isRequired>
              <FormLabel fontWeight="medium" color="whiteAlpha.900">Categoria</FormLabel>
              {!mostrarCampoNovaCategoria ? (
                <Flex direction={{ base: 'column', md: 'row' }} gap={3}>
                  <Select 
                    flex={{ md: 3 }} 
                    value={form.categoria} 
                    onChange={e => setForm({ ...form, categoria: e.target.value })} 
                    bg="gray.700" 
                    color="white" 
                    borderColor="gray.600"
                    _hover={{ borderColor: "yellow.300" }}
                    _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                  >
                    <option value="" style={{ background: '#2D3748', color: 'white' }}>Selecione uma categoria</option>
                    {categorias.map(cat => (
                      <option key={cat.id} value={cat.nome} style={{ background: '#2D3748', color: 'white' }}>{cat.nome}</option>
                    ))}
                  </Select>
                  <Button 
                    flex={{ md: 1 }} 
                    onClick={() => setMostrarCampoNovaCategoria(true)} 
                    leftIcon={<AddIcon />} 
                    colorScheme="yellow" 
                    variant="outline"
                    _hover={{ bg: "yellow.500", color: "white" }}
                  >
                    Nova
                  </Button>
                </Flex>
              ) : (
                <Box p={4} borderWidth="1px" borderRadius="md" borderColor="gray.600" bg="gray.700" mb={3}>
                  <FormControl mb={3}>
                    <FormLabel fontWeight="medium" color="whiteAlpha.900">Nome da Nova Categoria</FormLabel>
                    <Input 
                      value={novaCategoria} 
                      onChange={e => setNovaCategoria(e.target.value)} 
                      bg="gray.600" 
                      color="white" 
                      borderColor="gray.500"
                      _hover={{ borderColor: "yellow.300" }}
                      _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                      placeholder="Ex: Pomadas"
                      autoFocus
                    />
                  </FormControl>
                  <Flex gap={2} justify="flex-end">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      color="white" 
                      onClick={() => {
                        setMostrarCampoNovaCategoria(false);
                        setNovaCategoria('');
                      }}
                      _hover={{ bg: "gray.600" }}
                    >
                      Cancelar
                    </Button>
                    <Button 
                      size="sm" 
                      colorScheme="yellow" 
                      onClick={handleAddCategoria} 
                      isLoading={adicionandoCategoria}
                      isDisabled={!novaCategoria.trim()}
                    >
                      Adicionar
                    </Button>
                  </Flex>
                </Box>
              )}
            </FormControl>
            
            <FormControl mb={5}>
              <FormLabel fontWeight="medium" color="whiteAlpha.900">Descrição</FormLabel>
              <Textarea 
                value={form.descricao} 
                onChange={e => setForm({ ...form, descricao: e.target.value })} 
                bg="gray.700" 
                color="white" 
                borderColor="gray.600"
                _hover={{ borderColor: "yellow.300" }}
                _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                placeholder="Descreva o produto..."
                rows={3}
              />
            </FormControl>
            
            <FormControl mb={5}>
              <FormLabel fontWeight="medium" color="whiteAlpha.900">Imagem do Produto</FormLabel>
              <Flex direction={{ base: 'column', sm: 'row' }} align="center" gap={4}>
                <Box flex="1">
                  <Box 
                    borderWidth="2px" 
                    borderStyle="dashed" 
                    borderColor="gray.600" 
                    borderRadius="md" 
                    p={2} 
                    textAlign="center"
                    _hover={{ borderColor: "yellow.300" }}
                    position="relative"
                    overflow="hidden"
                  >
                    <Input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleImageUpload} 
                      height="100%"
                      width="100%"
                      position="absolute"
                      top="0"
                      left="0"
                      opacity="0"
                      aria-hidden="true"
                      cursor="pointer"
                    />
                    <Box py={4}>
                      <Text color="whiteAlpha.800" mb={2}>Arraste uma imagem ou clique para selecionar</Text>
                      <Button size="sm" colorScheme="yellow" variant="outline" pointerEvents="none">
                        Escolher Arquivo
                      </Button>
                    </Box>
                  </Box>
                </Box>
                
                {form.imagem_url ? (
                  <Box 
                    width="140px" 
                    height="140px" 
                    borderRadius="md" 
                    overflow="hidden" 
                    borderWidth="1px" 
                    borderColor="gray.600"
                  >
                    <Image 
                      src={form.imagem_url} 
                      alt={form.nome} 
                      width="100%" 
                      height="100%" 
                      objectFit="cover" 
                    />
                  </Box>
                ) : (
                  <Box 
                    width="140px" 
                    height="140px" 
                    borderRadius="md" 
                    bg="gray.700" 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="center"
                    borderWidth="1px" 
                    borderColor="gray.600"
                  >
                    <Text color="whiteAlpha.600" fontSize="sm" textAlign="center" px={2}>Sem imagem</Text>
                  </Box>
                )}
              </Flex>
            </FormControl>
            
            <FormControl display="flex" alignItems="center">
              <Switch 
                id="produto-ativo" 
                isChecked={form.ativo} 
                onChange={e => setForm({ ...form, ativo: e.target.checked })} 
                colorScheme="yellow" 
                size="lg"
                mr={3}
              />
              <FormLabel htmlFor="produto-ativo" mb={0} color="whiteAlpha.900" cursor="pointer">
                Produto ativo para venda
              </FormLabel>
            </FormControl>
          </ModalBody>
          
          <ModalFooter borderTopWidth="1px" borderColor="gray.700" py={4} px={6}>
            <Flex justify="space-between" w="100%" direction={{ base: 'column', sm: 'row' }} gap={3}>
              <Button 
                variant="outline" 
                onClick={closeModal} 
                color="white" 
                borderColor="gray.600"
                _hover={{ bg: "gray.700" }}
                width={{ base: "100%", sm: "auto" }}
              >
                Cancelar
              </Button>
              <Button 
                colorScheme="yellow" 
                onClick={handleSave} 
                isLoading={saving}
                leftIcon={editingId ? <EditIcon /> : <AddIcon />}
                width={{ base: "100%", sm: "auto" }}
                _hover={{ bg: "yellow.500" }}
              >
                {editingId ? 'Atualizar Produto' : 'Salvar Produto'}
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
      </Box>
    </AdminLayout>
  );
};

export default ProductsAdminPage;
